import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import type { components } from '@/../types/api'

export type Channel = components['schemas']['ChannelOut']
export type ChannelIn = components['schemas']['ChannelIn']
export type ChannelSettings = components['schemas']['ChannelSettingsOut']
export type ChannelSettingsIn = components['schemas']['ChannelSettingsIn']
export type FactoryUsage = components['schemas']['FactoryUsageOut']

export function useChannels() {
  return useQuery({
    queryKey: queryKeys.channels.list(),
    queryFn: async (): Promise<Channel[]> => {
      const { data } = await api.GET('/api/v1/channels', {
        params: { query: { limit: 200, offset: 0 } },
      })
      return data?.data ?? []
    },
    staleTime: 60_000, // channels rarely change; avoid refetch spam
  })
}

export function useChannel(slug: string | null | undefined) {
  const safeSlug = slug ?? ''
  return useQuery({
    queryKey: queryKeys.channels.byId(safeSlug),
    queryFn: async (): Promise<Channel> => {
      const { data } = await api.GET('/api/v1/channels/{slug}', {
        params: { path: { slug: safeSlug } },
      })
      if (!data) throw new Error('channel not found')
      return data.data
    },
    enabled: Boolean(slug),
    staleTime: 60_000,
  })
}

/**
 * Per-channel factory usage. The Factory selector consumes this to (a)
 * surface the 4 freshest factories for the active channel as a banner
 * and (b) reorder the catalog grid so factories the channel actually
 * uses float to the top. Sourced from ``video_projects`` server-side,
 * not the ephemeral jobs feed, so the signal stays stable as old jobs
 * get pruned.
 */
export function useChannelFactoryUsage(slug: string | null | undefined) {
  const safeSlug = slug ?? ''
  return useQuery({
    queryKey: queryKeys.channels.factoryUsage(safeSlug),
    queryFn: async (): Promise<FactoryUsage[]> => {
      const { data } = await api.GET('/api/v1/channels/{slug}/factory-usage', {
        params: { path: { slug: safeSlug } },
      })
      return data?.data ?? []
    },
    enabled: Boolean(slug),
    // Recalc cheap; 30s avoids hammering the agg query while a user
    // bounces between Factory and other tabs.
    staleTime: 30_000,
  })
}

export function useChannelSettings(slug: string | null | undefined) {
  const safeSlug = slug ?? ''
  return useQuery({
    queryKey: queryKeys.channels.settings(safeSlug),
    queryFn: async (): Promise<ChannelSettings> => {
      const { data } = await api.GET('/api/v1/channels/{slug}/settings', {
        params: { path: { slug: safeSlug } },
      })
      if (!data) throw new Error('settings not found')
      return data.data
    },
    enabled: Boolean(slug),
    staleTime: 30_000,
  })
}

/**
 * Create a new channel. Backend derives the slug from ``name``; ``slug``
 * is immutable after create. ``channel_tag`` and ``is_default`` are
 * optional. Returns the canonical ``ChannelOut`` (with the derived slug).
 */
export function useCreateChannel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: ChannelIn): Promise<Channel> => {
      const { data } = await api.POST('/api/v1/channels', { body: input })
      if (!data) throw new Error('create channel empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.channels.all })
    },
  })
}

export interface UpdateChannelInput {
  slug: string
  patch: ChannelIn
}

/**
 * Partial update of a channel. ``slug`` is immutable; passing it has no
 * effect. ``is_default=true`` will (server-side) flip the previous
 * default off — only one default per owner.
 */
export function useUpdateChannel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdateChannelInput): Promise<Channel> => {
      const { data } = await api.PUT('/api/v1/channels/{slug}', {
        params: { path: { slug: input.slug } },
        body: input.patch,
      })
      if (!data) throw new Error('update channel empty')
      return data.data
    },
    onSuccess: (data, vars) => {
      qc.setQueryData(queryKeys.channels.byId(vars.slug), data)
      void qc.invalidateQueries({ queryKey: queryKeys.channels.list() })
    },
  })
}

/**
 * Idempotent delete. Backend rejects (409) when this is the last
 * remaining channel — UI should disable the action in that case.
 */
export function useDeleteChannel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (slug: string): Promise<void> => {
      await api.DELETE('/api/v1/channels/{slug}', {
        params: { path: { slug } },
      })
    },
    onSuccess: (_data, slug) => {
      qc.removeQueries({ queryKey: queryKeys.channels.byId(slug) })
      void qc.invalidateQueries({ queryKey: queryKeys.channels.all })
    },
  })
}

export interface UpdateChannelSettingsInput {
  slug: string
  patch: Partial<ChannelSettingsIn>
}

/**
 * Partial update of a channel's settings (Discord webhook, default
 * language, video preset, etc.). Backend merges the patch with the
 * current row, returns the merged shape, and we mirror that into the
 * cache so the next read is instant.
 */
export function useUpdateChannelSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdateChannelSettingsInput): Promise<ChannelSettings> => {
      const { data } = await api.PUT('/api/v1/channels/{slug}/settings', {
        params: { path: { slug: input.slug } },
        // ChannelSettingsIn fields are all optional in the generated
        // schema (partial PUT), so Partial<...> aligns without a cast.
        body: input.patch,
      })
      if (!data) throw new Error('settings update empty')
      return data.data
    },
    onSuccess: (data, vars) => {
      qc.setQueryData(queryKeys.channels.settings(vars.slug), data)
    },
  })
}
